import { Trash2 } from "lucide-react";
import type React from "react";
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import type { AmortizationPlan, CorrectionPlan } from "../types";

interface PlanListProps {
  amortizationPlans: AmortizationPlan[];
  correctionPlans: CorrectionPlan[];
  onRemoveAmortization: (id: string) => void;
  onRemoveCorrection: (id: string) => void;
}

export const PlanList: React.FC<PlanListProps> = ({
  amortizationPlans,
  correctionPlans,
  onRemoveAmortization,
  onRemoveCorrection,
}) => {
  if (amortizationPlans.length === 0 && correctionPlans.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      {amortizationPlans.map((p) => (
        <div
          key={p.id}
          className="flex justify-between items-center bg-white border rounded-lg p-3 shadow-sm"
        >
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 text-sm">
            <div>
              <span className="text-gray-500 block text-xs">Amortizando</span>
              <Badge className="bg-blue-600 hover:bg-blue-700 text-white font-mono object-contain">
                R${" "}
                {p.value.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                })}
              </Badge>
            </div>
            <div>
              <span className="text-gray-500 block text-xs">por</span>
              <Badge className="bg-blue-600 hover:bg-blue-700 text-white uppercase">
                {p.type}
              </Badge>
            </div>
            <div>
              <span className="text-gray-500 block text-xs">
                a partir da
              </span>
              <Badge className="bg-blue-600 hover:bg-blue-700 text-white uppercase">
                PARCELA {p.startInstallment}
              </Badge>
            </div>
            {p.frequency === "multiplas" && (
              <div>
                <span className="text-gray-500 block text-xs">a cada</span>
                <Badge className="bg-blue-600 hover:bg-blue-700 text-white uppercase">
                  {p.interval} MESES
                </Badge>
              </div>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onRemoveAmortization(p.id)}
            className="text-red-500 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ))}
      {correctionPlans.map((p) => (
        <div
          key={p.id}
          className="flex justify-between items-center bg-white border rounded-lg p-3 shadow-sm"
        >
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 text-sm">
            <div>
              <span className="text-gray-500 block text-xs">Aplicando</span>
              <Badge className="bg-blue-600 hover:bg-blue-700 text-white font-mono">
                {p.valueAnual}%
              </Badge>
            </div>
            <div>
              <span className="text-gray-500 block text-xs">de</span>
              <Badge className="bg-blue-600 hover:bg-blue-700 text-white uppercase">
                CORREÇÃO
              </Badge>
            </div>
            <div>
              <span className="text-gray-500 block text-xs">em</span>
              <Badge className="bg-blue-600 hover:bg-blue-700 text-white uppercase">
                TODAS AS PARCELAS
              </Badge>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onRemoveCorrection(p.id)}
            className="text-red-500 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ))}
    </div>
  );
};
